import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";

import "react-widgets/styles.css";
import { Alert, Spinner, Button, Toast, ToastContainer } from "react-bootstrap";
import Table from "react-bootstrap/Table";
import PdfDocumentsModal from "../components/modals/PdfDocumentsModal";
import DocumentFolderDetailsModal from "../components/modals/DocumentFolderDetailsModal";
import ProcessingStatus from "../components/ProcessingStatus";
import { formatDate } from "../utils/dateUtils";
import logger from "../utils/logging";
import ApiService from "../services/ApiService";
import { DocumentFile, DocumentFolder, DeletionRule } from "../models/Document";
import DeleteConfirmationModal from "../components/modals/DeleteConfirmationModal";

interface FetchError {
    error: any;
    message: string;
    key: number;
}

interface DocumentListProps {
    apiService: ApiService;
}

const DocumentList: React.FC<DocumentListProps> = (props: DocumentListProps) => {
    const { apiService } = props;
    const { t } = useTranslation(["documents", "common"]);
    const navigate = useNavigate();

    const [searchParams, setSearchParams] = useSearchParams();

    const [fetchErrors, setFetchErrors] = React.useState<FetchError[]>([]);
    const [pdfFolders, setPdfFolders] = React.useState<DocumentFolder[] | null>(null);
    const [isFetching, setFetching] = React.useState<boolean>(false);
    const [pdfs, setPdfs] = React.useState<DocumentFile[] | null>(null);
    const [detailsModalFolder, setDetailsModalFolder] = React.useState<DocumentFolder | null>(null);
    const [folderForDeletion, setFolderForDeletion] = React.useState<DocumentFolder | null>(null);
    const [isDeleting, setIsDeleting] = React.useState<boolean>(false);

    const handleError = (error: any, message: string) => {
        logger.error("handledError: " + message + ":", error);
        setFetchErrors((prev) => {
            const normalizedMessage = error instanceof Error ? error.message : String(error ?? "");
            const alreadyPresent = prev.some((item) => {
                const existing = item.error instanceof Error ? item.error.message : String(item.error ?? "");
                return item.message === message && existing === normalizedMessage;
            });
            if (alreadyPresent) return prev;
            return [...prev, { error, message, key: Date.now() + Math.random() }];
        });
    };

    const removeError = (error: FetchError) => {
        setFetchErrors((prev) => prev.filter((item) => item.key !== error.key));
    };

    React.useEffect(() => {
        logger.info("start fetch documents batch list");
        setFetching(true);

        apiService
            .getDocumentFolders()
            .then(setPdfFolders)
            .catch((error) => handleError(error, t("errors.fetchFolders")))
            .finally(() => setFetching(false));
    }, [apiService]);

    React.useEffect(() => {
        const folderId = searchParams.get("fid");
        if (folderId && pdfFolders && Array.isArray(pdfFolders)) {
            for (const folder of pdfFolders) {
                if (folder.filename === folderId) {
                    openFolder(folder);
                }
            }
        }
    }, [pdfFolders]);

    const openFolder = (folder: DocumentFolder) => {
        if (folder == undefined) {
            setPdfs(null);
            return;
        }
        setFetching(true);

        apiService
            .getDocumentFiles(folder)
            .then((pdfs) => {
                setPdfs(pdfs);
            })
            .catch((error) => {
                setPdfs(null);
                handleError(error, t("errors.fetchFiles"));
            })
            .finally(() => setFetching(false));
    };


    const translateDeletionRule = (deletionRule: DeletionRule): string => {
        return t(`common:deletionRule.ruleOptions.${deletionRule}`);
    };

    const deleteDocumentFolder = async () => {
        try {
            if (folderForDeletion) {
                setIsDeleting(true);
                await apiService.deleteDocumentFolder(folderForDeletion.id);

                setFolderForDeletion(null);
                setDetailsModalFolder(null);

                window.location.reload(); // Simple refresh for now

                // TODO: instead of reload: fetch folders and setShowSuccessToast(true);
            } else {
                // TODO: show error
            }
        } catch (error) {
            handleError(error, t("errors.delete"));
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        setFolderForDeletion(null);
        setDetailsModalFolder(null);
    };

    return (
        <>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h1 className="display-4 mb-3">{t("pageTitle")}</h1>
                <Button variant="primary" onClick={() => navigate("/documents/create")}>
                    {t("createNew")}
                </Button>
            </div>

            {pdfFolders != null && pdfFolders.length > 0 ? (
                <Row>
                    <Col>
                        <Table striped hover={true} className="table-align-middle">
                            <thead>
                                <tr>
                                    <th scope="col">{t("common:headers.name")}</th>
                                    <th scope="col">{t("common:headers.createdAt")}</th>
                                    <th scope="col">{t("common:deletionRule.title")}</th>
                                    <th scope="col">{t("table.headers.processingStatus")}</th>
                                    <th scope="col" className="shrink">
                                        {t("common:headers.actions")}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {pdfFolders.map((folder) => (
                                    <tr key={folder.id} title={folder.id} onClick={() => openFolder(folder)}>
                                        <td>{folder.name}</td>
                                        <td>{formatDate(folder.createdAt)}</td>
                                        <td>{translateDeletionRule(folder.outputSettings.deletionRule)}</td>
                                        <td>
                                            <ProcessingStatus folder={folder} onShowDetails={setDetailsModalFolder} />
                                        </td>
                                        <td className="shrink">
                                            <button
                                                className="btn btn-outline-primary btn-sm"
                                                onClick={() => openFolder(folder)}
                                            >
                                                {t("table.actions.openFolder")}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Col>
                </Row>
            ) : (
                !isFetching && (
                    <div className="alert alert-info">
                        <h5>{t("emptyState.title")}</h5>
                        <p>{t("emptyState.message")}</p>
                    </div>
                )
            )}
            {isFetching && (
                <div className="d-flex gap-3 align-items-center">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">{t("common:loading")}</span>
                    </Spinner>
                    <span>{t("loading.documents")}</span>
                </div>
            )}
            {fetchErrors.length > 0 && (
                <>
                    {fetchErrors.map((fetchError) => (
                        <Alert
                            key={fetchError.key}
                            variant="danger"
                            onClose={() => removeError(fetchError)}
                            dismissible
                        >
                            <h1>{fetchError.message}</h1>
                            <p>{fetchError.error instanceof Error ? fetchError.error.message : String(fetchError.error)}</p>
                        </Alert>
                    ))}
                </>
            )}
            <PdfDocumentsModal
                show={pdfs != null && pdfs.length > 0}
                onClose={() => setPdfs(null)}
                pdfDocuments={pdfs ?? []}
            />
            <DocumentFolderDetailsModal
                show={detailsModalFolder !== null}
                onClose={() => setDetailsModalFolder(null)}
                folder={detailsModalFolder}
                documentErrorsProvider={() =>
                    detailsModalFolder
                        ? apiService.getDocumentErrors(detailsModalFolder)
                        : Promise.reject("detailsModalFolder is undefined")
                }
                folderDeleteHandler={setFolderForDeletion}
            />

            <DeleteConfirmationModal
                show={folderForDeletion != null}
                onHide={handleDeleteCancel}
                onConfirm={deleteDocumentFolder}
                title={t("delete.modal.title")}
                message={t("delete.modal.message")}
                itemLabel={t("delete.modal.itemLabel")}
                itemName={folderForDeletion?.name ?? undefined}
                isDeleting={isDeleting}
            />

            <ToastContainer position="top-end" className="p-3">
                <Toast
                    show={pdfs != null && pdfs.length == 0}
                    onClose={() => setPdfs(null)}
                    delay={3000}
                    autohide
                    bg="warning"
                >
                    <Toast.Header>
                        <strong className="me-auto">{t("toast.emptyDocuments.title")}</strong>
                    </Toast.Header>
                    <Toast.Body className="text-white">{t("toast.emptyDocuments.message")}</Toast.Body>
                </Toast>
            </ToastContainer>
        </>
    );
};

export default DocumentList;
